import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@store';
import { promoSelector, isZeroQtyOrderSelector } from '@store/slices/cart';
import './style.scss';

import { PromoCode } from '@components/PromoCode';
import { CommonButton } from '@components/common/CommonButton';
import { OrderInfo } from 'src/models/general';

type OrderSummarySectionProps = {
  orderInfo: OrderInfo;
  onProceedToCheckoutBtnClick: () => void;
  onContinueShoppingBtnClick: () => void;
  onPromoCodeApply: (value: string, cb: () => void) => void;
  onPromoCodeDelete: (prmId: string | undefined) => void;
  isFreeTrial: boolean;
  hasAllDigitalProducts: boolean;
  isLoading: boolean;
  currency: string;
};

export const OrderSummarySection = (props: OrderSummarySectionProps) => {
  const { t } = useTranslation();
  const {
    orderInfo, currency, isLoading, hasAllDigitalProducts,
  } = props;
  const { promoError, promoCode, isPromoLoading } = useAppSelector(promoSelector);
  const isOrderWithZeroQty = useAppSelector(isZeroQtyOrderSelector);

  return (
    <div className="summary">
      <h2 className="summary__title">
        {t('cart.summary.title')}
      </h2>
      <div className="summary__info">
        <div className="summary__info-product_total">
          <p className="text-body-2">
            {t('cart.summary.productTotal')}
          </p>
          <span className="summary__info-product_total--price text-body-1">
            {(isLoading || !orderInfo.productTotal) ? '--' : `${currency}${orderInfo.productTotal.toFixed(2)}`}
          </span>
        </div>
        <div className="summary__info-tax_info">
          <span className="text-body-2">
            {hasAllDigitalProducts ? (
              <i>{t('cart.summary.taxInformation')}</i>
            ) : (
              <i>{t('cart.summary.shippingAndTaxMessage')}</i>
            )}
          </span>
        </div>
        <div className="summary__info-promo_code">
          <PromoCode
            hideDiscountAmount={props.isFreeTrial}
            isLoading={isPromoLoading}
            promoError={promoError}
            promoCode={promoCode}
            onPromoCodeApply={props.onPromoCodeApply}
            onPromoCodeDelete={props.onPromoCodeDelete}
          />
        </div>
        {props.isFreeTrial && (
          <div className="summary__info-order_free">
            <p className="text-body-2">
              {t('cart.summary.recurringBill')}
            </p>
            <span className="summary__info-product_total--price text-body-1">
              {isLoading ? '--' : `${currency}${orderInfo.orderTotal?.toFixed(2)}`}
            </span>
          </div>
        )}
        <div className="summary__info-order_total">
          <p>
            {t('cart.summary.orderTotal')}
          </p>
          <span className="text-title-1">
            {isLoading ? '--' : `${currency}${orderInfo.orderTotal?.toFixed(2)}`}
          </span>
        </div>
        <CommonButton
          data-testid="summary__info-checkout--button"
          className="summary__info-checkout--button"
          type="primary"
          disabled={isOrderWithZeroQty}
          onClick={props.onProceedToCheckoutBtnClick}
        >
          {t('cart.summary.procceedToCheckoutBtnTitle')}
        </CommonButton>
        <CommonButton
          data-testid="summary__info-shopping--button"
          className="summary__info-shopping--button"
          type="secondary"
          onClick={props.onContinueShoppingBtnClick}
        >
          {t('cart.summary.continueShoppingBtnTitle')}
        </CommonButton>
      </div>
    </div>
  );
};
